import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Fuel, MapPin, ChevronRight, User, Users } from "lucide-react";
import { apiBaseUrl, authHeaders, isAdmin } from "./shared";
import LoadingState from "./LoadingState";
import Pagination from "./Pagination";
import RouteMonitorModal from "../../components/RouteMonitorModal";
import {
  fmtMoney, fmtPpg, fmtNum, varianceColor,
  Kpi, MethodologyButton, MethodologyDrawer,
} from "./ComplianceUI";

/*
  Fuel Compliance — load-level price-efficiency, drilled Dispatcher → Driver → Load.
  Role-gated (no toggle): admins start at Dispatchers; a dispatcher starts at their
  own Drivers (scope=dispatcher). Driver = the truck's CURRENT assignment (historical
  attribution caveat shown). Backed by /scripted/fleet/fuel-compliance-v2.
*/

const FUEL_METHODOLOGY = [
  { term: "Price vs AIVAN", text: "Actual fuel-stop reference price vs AIVAN's recommended stations repriced at the same fueling time (execution-era counterfactual). Isolates the driver's station/price choice from market movement since planning. $/gal." },
  { term: "Estimated Price Impact", text: "Comparable gallons (smaller of planned vs detected) × Price vs AIVAN, over completed (delivery-confirmed) loads only. The gallons difference itself is never dollarized." },
  { term: "Reference price", text: "The Pilot contracted station price applicable at the fill time — not a receipt/paid price and not today's price." },
  { term: "Driver attribution", text: "Driver = the truck's CURRENT assigned driver (we don't yet store a historical driver-of-record per load), so driver-level figures are indicative." },
  { term: "Under review", text: "Prices from the 2026-09-17..22 Pilot window are held out until the source data is confirmed." },
];

// Monday-based week nav. "This week" = Mon..today (clamped); arrows step weeks;
// an explicit All-time escape hatch. Bounds are local wall-clock Y-M-D (server is UTC;
// a few hours' slack at the week edge is acceptable for a weekly filter).
const ymd = (d) => d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
function mondayOf(d) { const x = new Date(d); const dow = (x.getDay() + 6) % 7; x.setDate(x.getDate() - dow); x.setHours(0, 0, 0, 0); return x; }
const THIS_MON = mondayOf(new Date());
function weekBounds(offset) {
  const mon = new Date(THIS_MON); mon.setDate(mon.getDate() + offset * 7);
  const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const end = offset === 0 ? today : sun;
  return { mon, sun, end, startIso: ymd(mon) + "T00:00:00", endIso: ymd(end) + "T23:59:59" };
}
const monDay = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
const centsTxt = (d) => (d == null ? "—" : (d > 0 ? "+" : d < 0 ? "-" : "") + Math.round(Math.abs(d) * 100) + "¢/gal");
const fmtDate = (iso) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d) ? "—" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" }); };
const LOADS_PER_PAGE = 12;

// group metrics computed identically to the fleet KPIs (comparable-gal weighted rate;
// dollar over delivery-confirmed only)
function agg(evs) {
  const assess = evs.filter((e) => e.status === "assessable");
  const wd = assess.reduce((s, e) => s + (e.comparable_gallons || 0), 0);
  const wn = assess.reduce((s, e) => s + (e.comparable_gallons || 0) * (e.delta_cf || 0), 0);
  const dollarPop = assess.filter((e) => e.dollar_eligible && e.impact != null);
  const reviewAssessed = assess.filter((e) => e.ui_status === "review").length;
  return {
    assessed: assess.length,
    // Compliance % = share of ASSESSED loads that were on plan (not flagged for review).
    pct: assess.length ? Math.round(100 * (assess.length - reviewAssessed) / assess.length) : null,
    onPlan: assess.length - reviewAssessed,
    rate: wd ? wn / wd : null,
    impact: dollarPop.length ? dollarPop.reduce((s, e) => s + e.impact, 0) : null,
    needs: evs.filter((e) => e.ui_status === "review").length,
    cheaper: evs.filter((e) => e.ui_status === "cheaper").length,
    total: evs.length,
  };
}
// compliance-% color tier (separate from the $ variance color)
function pctColor(p) { return p == null ? "var(--muted)" : p >= 90 ? "#1b8a4b" : p >= 75 ? "#b4741a" : "#c0392b"; }
function groupBy(evs, idKey, nameKey) {
  const m = new Map();
  for (const e of evs) {
    const id = e[idKey] || "__none__";
    if (!m.has(id)) m.set(id, { id, name: e[nameKey] || "Unassigned", evs: [] });
    m.get(id).evs.push(e);
  }
  return [...m.values()].map((g) => ({ ...g, m: agg(g.evs) }))
    .sort((a, b) => (b.m.needs - a.m.needs) || (Math.abs(b.m.impact || 0) - Math.abs(a.m.impact || 0)) || a.name.localeCompare(b.name));
}
const STATUS_RANK = { review: 0, check: 1, cheaper: 2, neutral: 3 };

export default function FuelCompliance() {
  const admin = isAdmin();
  const scope = admin ? "admin" : "dispatcher";
  const dispatcherId = admin ? "" : (localStorage.getItem("id") || "");

  const [weekOffset, setWeekOffset] = useState(0); // 0 = this week (default)
  const [allTime, setAllTime] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [methodOpen, setMethodOpen] = useState(false);
  const [openRoute, setOpenRoute] = useState(null);
  const [detail, setDetail] = useState(null);
  const [expDisp, setExpDisp] = useState(() => new Set());   // expanded dispatcher ids
  const [expDrv, setExpDrv] = useState(() => new Set());     // expanded "dispId|drvId" keys
  const toggle = (setter, key) => setter((s) => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  useEffect(() => {
    setLoading(true); setError(null);
    const params = { scope };
    if (allTime) { params.start = "2020-01-01"; }
    else { const b = weekBounds(weekOffset); params.start = b.startIso; params.end = b.endIso; }
    if (dispatcherId) params.dispatcher_id = dispatcherId;
    axios.get(`${apiBaseUrl}/scripted/fleet/fuel-compliance-v2`, { ...authHeaders(), params })
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [weekOffset, allTime, scope, dispatcherId]);

  // collapse the accordion whenever the time range changes
  useEffect(() => { setExpDisp(new Set()); setExpDrv(new Set()); }, [weekOffset, allTime, admin]);

  const k = data?.kpis || {};
  const cov = data?.coverage || {};
  const events = useMemo(() => data?.events || [], [data]);

  const fleet = useMemo(() => agg(events), [events]);
  // Dispatcher -> Driver -> Load tree, each level sorted worst-first.
  const tree = useMemo(() => groupBy(events, "dispatcher_id", "dispatcher_name").map((d) => ({
    ...d,
    drivers: groupBy(d.evs, "driver_id", "driver_name").map((dr) => ({
      ...dr,
      loads: [...dr.evs].sort((a, b) => (STATUS_RANK[a.ui_status] - STATUS_RANK[b.ui_status]) || (Math.abs(b.impact || 0) - Math.abs(a.impact || 0)) || String(b.pickup_at || "").localeCompare(String(a.pickup_at || ""))),
    })),
  })), [events]);

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}><Fuel size={20} style={{ verticalAlign: "-3px", marginRight: 8 }} />Fuel Compliance</h1>
          <div style={styles.subtitle}>
            {admin ? "Are dispatchers keeping their drivers fueling where AIVAN recommends?"
                   : "Your drivers — are they fueling where AIVAN recommends?"}
          </div>
        </div>
        {(() => { const b = weekBounds(weekOffset); const atThisWeek = !allTime && weekOffset >= 0; return (
        <div style={styles.dateRange}>
          <div style={styles.weeknav}>
            <button type="button" style={styles.wkBtn} aria-label="Previous week"
              onClick={() => { setAllTime(false); setWeekOffset((o) => o - 1); }}>‹</button>
            <div style={styles.wkRange}>
              <div style={styles.wkLbl}>{allTime ? "All time" : (weekOffset === 0 ? "This week" : `${monDay(b.mon)} – ${monDay(b.sun)}`)}</div>
              <div style={styles.wkRng}>{allTime ? "everything" : `${monDay(b.mon)} – ${monDay(b.end)}, ${b.end.getFullYear()}`}</div>
            </div>
            <button type="button" style={{ ...styles.wkBtn, ...(atThisWeek ? { opacity: 0.35, cursor: "not-allowed" } : {}) }}
              disabled={atThisWeek} aria-label="Next week"
              onClick={() => { setAllTime(false); setWeekOffset((o) => Math.min(0, o + 1)); }}>›</button>
          </div>
          <button type="button" style={{ ...styles.presetBtn, ...(!allTime && weekOffset === 0 ? styles.presetBtnActive : {}) }}
            onClick={() => { setAllTime(false); setWeekOffset(0); }}>This Week</button>
          <button type="button" style={{ ...styles.presetBtn, ...(allTime ? styles.presetBtnActive : {}) }}
            onClick={() => setAllTime(true)}>All time</button>
          <MethodologyButton onClick={() => setMethodOpen(true)} />
        </div> ); })()}
      </div>

      {error && <div style={styles.errorBox}>Couldn't load fuel compliance: {error}</div>}
      {loading && <LoadingState label="Loading fuel compliance…" />}

      {!loading && !error && data && (
        <>
          <div style={styles.summaryRow}>
            <Kpi label="Fleet compliance" value={fleet.pct == null ? "—" : `${fleet.pct}%`} valueColor={pctColor(fleet.pct)} sub={`${fmtNum(fleet.onPlan)} of ${fmtNum(fleet.assessed)} assessed loads on plan`} />
            <Kpi label="Price vs AIVAN" value={centsTxt(k.price_vs_aivan)} valueColor={varianceColor(k.price_vs_aivan)} sub="Chosen stations vs AIVAN's plan, same fueling time" />
            <Kpi label="Estimated price impact" value={fmtMoney(k.estimated_price_impact)} valueColor={varianceColor(k.estimated_price_impact)} sub={`across ${fmtNum(k.estimated_price_impact_loads)} completed loads`} />
            <Kpi label="Needs attention" value={fmtNum(k.needs_attention)} sub="Materially more expensive" tone={k.needs_attention ? "warn" : "neutral"} />
          </div>

          <div style={styles.scopeNote}>
            Assessed <b>{fmtNum(cov.rate_assessable_loads)}</b> of <b>{fmtNum(cov.loads_with_attributed_fills)}</b> loads with fueling; {fmtNum(cov.suspect_loads)} held for price review, {fmtNum(cov.feasible_no_stop_loads)} feasible no-stop, {fmtNum(cov.no_canonical_plan_loads)} without an AIVAN plan.
          </div>

          {/* Accordion: Dispatcher → Driver → Load (inline expand, nothing leaves the view) */}
          <ListBand title={admin ? "Dispatchers → Drivers → Loads" : "My drivers → Loads"}
            note="Compliance = % of assessed loads on plan. Click a row to expand; open a load for its route.">
            {admin
              ? tree.map((d) => (
                  <DispBlock key={d.id} d={d} open={expDisp.has(d.id)} onToggle={() => toggle(setExpDisp, d.id)}
                    expDrv={expDrv} onToggleDrv={(key) => toggle(setExpDrv, key)} onLoad={setDetail} />))
              : (tree[0]?.drivers || []).map((dr) => (
                  <DrvBlock key={dr.id} dkey={"self|" + dr.id} dr={dr} open={expDrv.has("self|" + dr.id)}
                    onToggle={() => toggle(setExpDrv, "self|" + dr.id)} onLoad={setDetail} />))}
            {events.length === 0 && <div style={styles.empty}>No fueling activity in this range.</div>}
          </ListBand>
        </>
      )}

      <MethodologyDrawer open={methodOpen} onClose={() => setMethodOpen(false)} title="How Fuel Compliance is calculated" items={FUEL_METHODOLOGY} />
      {detail && <DetailDrawer e={detail} onClose={() => setDetail(null)} onViewRoute={(r) => { setOpenRoute(r); setDetail(null); }} />}
      {openRoute && <RouteMonitorModal loadId={openRoute.loadId} unitNumber={openRoute.unitNumber} onClose={() => setOpenRoute(null)} />}
    </div>
  );
}

// Full-width list section. NOTE: the shared <Band> lays children in a KPI grid
// (repeat(auto-fit, minmax(190px,1fr))) which crushes full-width rows; list rows
// need a flex column instead (matches TollCompliance's truckList pattern).
function ListBand({ title, note, children }) {
  return (
    <section style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, margin: "0 2px 8px", flexWrap: "wrap" }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--muted)" }}>{title}</span>
        {note && <span style={{ fontSize: 11.5, color: "var(--muted)" }}>{note}</span>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </section>
  );
}

function Metric({ label, value, color }) {
  return <div style={styles.metric}><div style={styles.metricLabel}>{label}</div><div style={{ ...styles.metricValue, color: color || "var(--ink)" }}>{value}</div></div>;
}

// Expandable header row (dispatcher or driver) — compliance % shown per row.
function ExpRow({ icon, name, meta, m, open, onClick, indent }) {
  return (
    <div style={{ ...styles.groupRow, ...(indent ? { marginLeft: 22, background: "var(--card-2)" } : {}) }} onClick={onClick}>
      <div style={styles.groupLeft}>
        <ChevronRight size={16} style={{ color: "var(--muted)", flexShrink: 0, transition: "transform .15s ease", transform: open ? "rotate(90deg)" : "none" }} />
        <div style={styles.groupIcon}>{icon}</div>
        <div style={{ minWidth: 0 }}>
          <div style={styles.groupName}>{name}</div>
          <div style={styles.groupMeta}>{meta}</div>
        </div>
      </div>
      <div style={styles.groupMetrics}>
        <Metric label="Compliance" value={m.pct == null ? "—" : `${m.pct}%`} color={pctColor(m.pct)} />
        <Metric label="Price vs AIVAN" value={centsTxt(m.rate)} color={varianceColor(m.rate)} />
        <Metric label="Est. impact" value={m.impact == null ? "—" : fmtMoney(m.impact)} color={varianceColor(m.impact)} />
        {m.needs > 0 && <span style={styles.needsPill}>{m.needs} to review</span>}
      </div>
    </div>
  );
}

function DispBlock({ d, open, onToggle, expDrv, onToggleDrv, onLoad }) {
  return (
    <div>
      <ExpRow icon={<Users size={16} />} name={d.name} meta={`${d.drivers.length} driver${d.drivers.length === 1 ? "" : "s"} · ${d.m.assessed} assessed`} m={d.m} open={open} onClick={onToggle} />
      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
          {d.drivers.map((dr) => (
            <DrvBlock key={dr.id} dkey={d.id + "|" + dr.id} dr={dr} open={expDrv.has(d.id + "|" + dr.id)} onToggle={() => onToggleDrv(d.id + "|" + dr.id)} onLoad={onLoad} indent />
          ))}
        </div>
      )}
    </div>
  );
}

function DrvBlock({ dr, open, onToggle, onLoad, indent }) {
  return (
    <div>
      <ExpRow icon={<User size={16} />} name={dr.name} meta={`${dr.loads.length} load${dr.loads.length === 1 ? "" : "s"} · ${dr.m.assessed} assessed`} m={dr.m} open={open} onClick={onToggle} indent={indent} />
      {open && (
        <div style={{ ...styles.loadList, marginTop: 8, marginLeft: indent ? 44 : 22 }}>
          {dr.loads.map((e) => <LoadRow key={e.load_id} e={e} onClick={() => onLoad(e)} />)}
          {dr.loads.length === 0 && <div style={styles.empty}>No loads.</div>}
        </div>
      )}
    </div>
  );
}

function LoadRow({ e, onClick }) {
  return (
    <div style={styles.loadRow} onClick={onClick}>
      <div style={{ minWidth: 0 }}>
        <div style={styles.loadLane}>{e.lane || "Load"}</div>
        <div style={styles.loadSub}>{fmtDate(e.pickup_at)} · {e.n_planned_stops} planned / {e.n_fills} fill{e.n_fills === 1 ? "" : "s"}</div>
      </div>
      <div style={styles.loadRight}>
        <div style={{ ...styles.loadDelta, color: e.price_difference != null ? varianceColor(e.price_difference) : "var(--muted)" }}>{e.price_difference == null ? "—" : centsTxt(e.price_difference)}</div>
        <div style={{ ...styles.loadImpact, color: e.impact != null ? varianceColor(e.impact) : "var(--muted)" }}>{e.impact == null ? "—" : fmtMoney(e.impact)}</div>
        <StatusChip s={e.ui_status} />
        <ChevronRight size={16} style={{ color: "var(--muted)" }} />
      </div>
    </div>
  );
}

function StatusChip({ s }) {
  const m = {
    review: ["#d63b3b", "rgba(214,59,59,.10)", "Review"],
    cheaper: ["#16a34a", "rgba(22,163,74,.10)", "Cheaper"],
    neutral: ["#16a34a", "rgba(22,163,74,.10)", "Followed"],
    suspect: ["#8a93a2", "rgba(138,147,162,.12)", "Under review"],
    check: ["#8a93a2", "rgba(138,147,162,.12)", "Check data"],
  }[s] || ["#8a93a2", "rgba(138,147,162,.12)", s];
  return <span style={{ color: m[0], background: m[1], fontSize: 11.5, fontWeight: 700, padding: "3px 9px", borderRadius: 999, whiteSpace: "nowrap" }}>{m[2]}</span>;
}

function DetailDrawer({ e, onClose, onViewRoute }) {
  return (
    <div style={styles.scrim} onClick={onClose}>
      <div style={styles.drawer} onClick={(ev) => ev.stopPropagation()}>
        <div style={styles.drawerHead}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>{e.lane || "Load"}</div>
          <button style={styles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{ color: "var(--muted)", fontSize: 12.5, marginBottom: 12 }}>
          {fmtDate(e.pickup_at)} · Driver {e.driver_name || "—"} · Dispatcher {e.dispatcher_name || "—"}
        </div>
        <div style={styles.vs}>
          <div style={styles.vsBox}><div style={styles.vsCap}>AIVAN's strategy</div><div style={styles.vsMain}>{e.n_planned_stops} planned stop{e.n_planned_stops === 1 ? "" : "s"}</div><div style={styles.vsSub}>{fmtNum(e.Gp)} gal · {fmtPpg(e.planned_avg_price_cf)} at fueling time</div></div>
          <div style={styles.vsBox}><div style={styles.vsCap}>Actual fueling</div><div style={styles.vsMain}>{e.n_fills} fill{e.n_fills === 1 ? "" : "s"}</div><div style={styles.vsSub}>~{fmtNum(e.Ga)} gal · {fmtPpg(e.actual_avg_price)}</div></div>
        </div>
        <div style={styles.hl}>
          <div style={styles.hlBox}><div style={styles.hlCap}>Price vs AIVAN</div><div style={{ ...styles.hlVal, color: varianceColor(e.price_difference) }}>{e.price_difference == null ? "—" : centsTxt(e.price_difference)}</div></div>
          <div style={styles.hlBox}><div style={styles.hlCap}>Estimated price impact</div><div style={{ ...styles.hlVal, color: varianceColor(e.impact) }}>{e.impact == null ? "—" : fmtMoney(e.impact)}</div></div>
        </div>
        {e.ui_status === "suspect" && <div style={styles.warn}>Prices for this load fall in a Pilot window under review — no figure shown until confirmed.</div>}
        {e.reason === "gallons_uncertain" && <div style={styles.warn}>Fuel amount uncertain — per-gallon difference shown, but no dollar impact estimated.</div>}
        <div style={styles.why}>
          {e.ui_status === "cheaper" ? "At fuel time, the station chosen was cheaper than AIVAN's recommended station — useful optimizer feedback."
            : e.price_difference == null ? "Not enough trustworthy price evidence to compare this load."
            : `Actual fueling was ${Math.round(Math.abs(e.price_difference) * 100)}¢/gal ${e.price_difference >= 0 ? "more expensive" : "cheaper"} than AIVAN's plan, priced at the same fueling times.`}
          <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--muted)" }}>
            Original plan price when planned: {fmtPpg(e.planned_avg_price_plan)} (planning quality).{e.provenance?.optimizer_type ? ` · Plan by ${e.provenance.optimizer_type}.` : ""}
          </div>
        </div>
        <button style={styles.routeBtn} onClick={() => onViewRoute({ loadId: e.load_id, unitNumber: e.truck })}><MapPin size={13} /> View route</button>
      </div>
    </div>
  );
}

const styles = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 20, flexWrap: "wrap" },
  title: { fontSize: 22, fontWeight: 800, color: "var(--ink)", margin: 0 },
  subtitle: { fontSize: 13, color: "var(--muted)", marginTop: 4, maxWidth: 680 },
  dateRange: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0, flexWrap: "wrap" },
  presetBtn: { fontSize: 11.5, fontWeight: 600, color: "var(--ink-2)", background: "var(--card)", border: "1px solid var(--line)", borderRadius: 8, padding: "7px 10px", cursor: "pointer", whiteSpace: "nowrap" },
  presetBtnActive: { color: "var(--brand)", borderColor: "var(--brand)", background: "rgba(37,99,235,0.06)" },
  weeknav: { display: "flex", alignItems: "center", gap: 2, background: "var(--card)", border: "1px solid var(--line)", borderRadius: 10, padding: 3 },
  wkBtn: { fontSize: 18, fontWeight: 700, lineHeight: 1, color: "var(--ink)", background: "transparent", border: 0, cursor: "pointer", borderRadius: 7, padding: "4px 10px" },
  wkRange: { minWidth: 150, textAlign: "center", padding: "2px 6px" },
  wkLbl: { fontSize: 12.5, fontWeight: 800, color: "var(--ink)", fontVariantNumeric: "tabular-nums" },
  wkRng: { fontSize: 10.5, color: "var(--muted)", fontVariantNumeric: "tabular-nums" },
  errorBox: { background: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.2)", color: "#dc2626", borderRadius: "var(--radius-md)", padding: "12px 16px", fontSize: 13, marginBottom: 16 },
  summaryRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 16 },
  scopeNote: { background: "var(--card-2)", border: "1px dashed var(--line)", borderRadius: "var(--radius-md)", padding: "10px 14px", fontSize: 12.5, color: "var(--muted)", marginBottom: 16, lineHeight: 1.5 },
  crumbs: { display: "flex", alignItems: "center", gap: 6, marginBottom: 12, flexWrap: "wrap" },
  crumb: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, fontWeight: 600, color: "var(--brand)", background: "transparent", border: 0, cursor: "pointer", padding: "2px 4px" },
  crumbActive: { color: "var(--ink)", cursor: "default" },
  empty: { color: "var(--muted)", fontSize: 13, padding: "16px 4px", textAlign: "center" },
  groupRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", padding: "12px 16px", marginBottom: 8, cursor: "pointer" },
  groupLeft: { display: "flex", alignItems: "center", gap: 12, minWidth: 0 },
  groupIcon: { width: 34, height: 34, borderRadius: "50%", background: "var(--card-2)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted)", flexShrink: 0 },
  groupName: { fontSize: 14.5, fontWeight: 700, color: "var(--ink)" },
  groupMeta: { fontSize: 12, color: "var(--muted)", marginTop: 1 },
  groupMetrics: { display: "flex", alignItems: "center", gap: 18, flexShrink: 0 },
  metric: { textAlign: "right", minWidth: 84 },
  metricLabel: { fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--muted)", fontWeight: 700 },
  metricValue: { fontSize: 15, fontWeight: 800, fontVariantNumeric: "tabular-nums", marginTop: 2 },
  needsPill: { fontSize: 11.5, fontWeight: 700, color: "#b45309", background: "rgba(217,119,6,0.12)", padding: "4px 9px", borderRadius: 999, whiteSpace: "nowrap" },
  loadList: { display: "flex", flexDirection: "column", gap: 8 },
  loadRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 14, background: "var(--card)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", padding: "11px 14px", cursor: "pointer" },
  loadLane: { fontSize: 13.5, fontWeight: 700, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  loadSub: { fontSize: 12, color: "var(--muted)", marginTop: 2 },
  loadRight: { display: "flex", alignItems: "center", gap: 16, flexShrink: 0 },
  loadDelta: { fontSize: 14, fontWeight: 700, fontVariantNumeric: "tabular-nums", minWidth: 70, textAlign: "right" },
  loadImpact: { fontSize: 14, fontWeight: 700, fontVariantNumeric: "tabular-nums", minWidth: 60, textAlign: "right" },
  scrim: { position: "fixed", inset: 0, background: "rgba(10,14,22,.45)", zIndex: 60, display: "flex", justifyContent: "flex-end" },
  drawer: { width: "min(460px,94vw)", height: "100%", background: "var(--card)", borderLeft: "1px solid var(--line)", padding: 20, overflowY: "auto", boxShadow: "-12px 0 40px rgba(0,0,0,.18)" },
  drawerHead: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  closeBtn: { border: 0, background: "var(--card-2)", color: "var(--ink)", width: 30, height: 30, borderRadius: 8, cursor: "pointer", fontSize: 16 },
  vs: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, margin: "14px 0" },
  vsBox: { background: "var(--card-2)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", padding: 12 },
  vsCap: { fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted)", fontWeight: 700 },
  vsMain: { fontWeight: 800, fontSize: 15, marginTop: 4, color: "var(--ink)" },
  vsSub: { fontSize: 12.5, color: "var(--muted)", marginTop: 3 },
  hl: { display: "flex", gap: 12, margin: "4px 0" },
  hlBox: { flex: 1, background: "var(--card-2)", border: "1px solid var(--line)", borderRadius: "var(--radius-md)", padding: 12, textAlign: "center" },
  hlCap: { fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--muted)", fontWeight: 700 },
  hlVal: { fontSize: 22, fontWeight: 800, marginTop: 4 },
  warn: { marginTop: 12, background: "rgba(217,119,6,0.10)", color: "#b45309", borderRadius: 10, padding: "9px 12px", fontSize: 12.5, fontWeight: 600 },
  why: { background: "rgba(37,99,235,0.06)", borderRadius: "var(--radius-md)", padding: "12px 14px", fontSize: 13, marginTop: 14, color: "var(--ink)" },
  routeBtn: { display: "inline-flex", alignItems: "center", gap: 6, marginTop: 14, fontSize: 12.5, fontWeight: 600, color: "var(--brand)", background: "rgba(37,99,235,0.06)", border: "1px solid rgba(37,99,235,0.25)", borderRadius: 8, padding: "8px 12px", cursor: "pointer" },
};
